import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import intlTelInput, { type Iti } from 'intl-tel-input';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ]
})
export class PhoneInputComponent implements AfterViewInit, ControlValueAccessor, OnDestroy, Validator {
  @ViewChild('phoneInput', { static: true }) phoneInput: ElementRef<HTMLInputElement>;

  @Input() inputId = 'phoneNumber';
  @Input() placeholder?: string;

  disabled = false;

  private instance: Iti | null = null;
  private pendingValue = '';
  private utilsReady = false;
  private writingValue = false;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  private readonly handleCountryChange = (): void => {
    if (!this.writingValue) {
      this.emitCurrentValue();
    }
    this.onValidatorChange();
  };

  ngAfterViewInit(): void {
    this.instance = intlTelInput(this.phoneInput.nativeElement, {
      // countryOrder: ['us', 'gb'],
      countrySearch: false,
      countrySelectorMode: 'DROPDOWN',
      customPlaceholder: exampleNumber => this.placeholder ?? exampleNumber,
      formatAsYouType: true,
      initialCountry: 'us',
      loadUtils: () => import('intl-tel-input/utils'),
      matchDropdownWidth: false,
      numberDisplayFormat: 'NATIONAL',
      // onlyCountries: ['us', 'gb'],
      placeholderNumberType: 'MOBILE',
      separateDialCode: true,
      strictMode: true
    });

    this.phoneInput.nativeElement.addEventListener('countrychange', this.handleCountryChange);
    this.instance.setDisabled(this.disabled);

    if (this.pendingValue) {
      this.setPluginValue(this.pendingValue);
    }

    this.instance.promise.then(() => {
      this.utilsReady = true;
      this.emitCurrentValue();
      this.onValidatorChange();
    });
  }

  ngOnDestroy(): void {
    this.phoneInput.nativeElement.removeEventListener('countrychange', this.handleCountryChange);
    this.instance?.destroy();
  }

  writeValue(value: string | null): void {
    this.pendingValue = value ?? '';
    if (this.instance) {
      this.setPluginValue(this.pendingValue);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.instance?.setDisabled(disabled);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    return this.utilsReady && this.instance?.isValidNumber()
      ? null
      : { phoneNumber: true };
  }

  updateValue(): void {
    this.emitCurrentValue();
  }

  markAsTouched(): void {
    this.onTouched();
  }

  private emitCurrentValue(): void {
    this.onChange(this.currentValue());
  }

  private currentValue(): string {
    if (!this.instance) {
      return this.phoneInput.nativeElement.value;
    }

    const selectedCountry = this.instance.getSelectedCountry();
    const dialCode = selectedCountry?.dialCode ? `+${selectedCountry.dialCode}` : '';

    return this.utilsReady
      ? this.instance.getNumber() || dialCode
      : `${dialCode}${this.phoneInput.nativeElement.value.replace(/\D/g, '')}`;
  }

  private setPluginValue(value: string): void {
    if (!this.instance) {
      return;
    }

    this.writingValue = true;
    this.instance.setNumber(value);
    this.writingValue = false;
  }
}
